<template>
  <main class="wallet-withdraw-page">
    <AppNavBar :title="t('home.withdraw')" />

    <template v-if="coinList.length || withdrawStore.loading">
      <section v-if="coinList.length" class="wallet-card selector-card" @click="goSelectCoin">
        <div class="coin-title">
          <app-image :src="selectedCoin.logo || usdtIcon" alt="" />
          <div data-strong>{{ selectedCoin.symbol || '--' }}</div>
        </div>
        <span>
          {{ t('wallet.chooseCoin') }}
          <i class="selector-card__arrow" aria-hidden="true" />
        </span>
      </section>

      <section v-if="chains.length" class="wallet-card network-card">
        <h2>{{ t('wallet.chooseNetwork') }}</h2>
        <div class="network-select" @click.stop="toggleChainPopup">
          <span>{{ selectedChainLabel }}</span>
          <van-icon name="arrow-down" />
        </div>
        <div v-if="showChainPopup" class="network-options">
          <div
            v-for="option in chainOptions"
            :key="`${option.text}-${option.value}`"
            :class="['network-options__item', { 'network-options__item--active': selectedChainIndex === option.value }]"
            @click.stop="selectChain(option.value)"
          >
            {{ option.text }}
          </div>
        </div>
      </section>
      <div v-if="showChainPopup" class="network-mask" @click="showChainPopup = false" />

      <section class="wallet-card input-card">
        <h2>{{ t('wallet.withdrawAddress') }}</h2>
        <div class="input-shell">
          <input
            v-model="addressTo"
            type="text"
            :placeholder="t('wallet.enterWithdrawAddress')"
          />
          <button type="button" class="scan-btn" @click="openScanner">
            <app-image src="@/assets/images/scancode_icon3.png" alt="" />
          </button>
        </div>
      </section>

      <section v-if="coinList.length" class="wallet-card">
        <div class="amount-title">
          <h2>{{ t('wallet.withdrawAmountLabel') }}</h2>
          <span>{{ t('wallet.available') }} {{ selectedCoin.balanceNormal || selectedCoin.balance || '0' }} {{ selectedCoin.symbol }}</span>
        </div>

        <div class="amount-field">
          <input
            v-model="amount"
            type="number"
            inputmode="decimal"
            :placeholder="t('wallet.enterWithdrawAmount')"
          />
          <div class="amount-side">
            <span class="amount-symbol">{{ selectedCoin.symbol }}</span>
            <button class="all-btn" type="button" @click="fillAll">{{ t('wallet.all') }}</button>
          </div>
        </div>

        <div class="dashed-divider" />
        <div class="detail-row">
          <span>{{ t('wallet.fee') }}</span>
          <div data-strong>{{ feeRateText }}</div>
        </div>
        <div class="dashed-divider" />
        <div class="detail-row">
          <span>{{ t('wallet.received') }}</span>
          <div data-strong>{{ receivedAmount }} {{ selectedCoin.symbol }}</div>
        </div>
      </section>

      <van-button block round type="primary" class="submit-btn" @click="confirmWithdraw">
        {{ t('wallet.submitWithdraw') }}
      </van-button>

      <section class="wallet-card notes-card">
        <h2>{{ t('wallet.withdrawNotes') }}</h2>
        <p>
          {{ t('wallet.withdrawNote1Prefix') }}
          <span data-strong>{{ withdrawMinText }}</span>
        </p>
        <p>{{ t('wallet.withdrawNote2') }}</p>
        <p>
          {{ t('wallet.withdrawNote3Prefix') }}
          <span data-strong>0-2</span>
          {{ t('wallet.withdrawNote3Suffix') }}
        </p>
        <p>{{ t('wallet.withdrawNote4') }}</p>
      </section>
    </template>

    <section v-else class="empty-wrap">
      <AppEmptyState plain />
      <span>{{ t('wallet.withdrawUnavailable') }}</span>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { closeToast, showConfirmDialog, showFailToast, showLoadingToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { pcCardAssetWithdraw } from '@/api/wallet';
import { useWalletScanner } from '@/composables/useWalletScanner';
import { normalizeFeeRate } from '@/view/card/utils';
import { useProfileStore } from '@/stores/profile';
import { useWalletWithdrawStore } from '@/stores/wallet-withdraw';
import { formatTokenAmount } from '@/utils/format';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import usdtIcon from '@/assets/images/usdt.png';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const profileStore = useProfileStore();
const withdrawStore = useWalletWithdrawStore();
const pageState = computed(() => getRouteState(route, 'wallet-withdraw', {}));

const selectedChainIndex = ref(0);
const showChainPopup = ref(false);
const addressTo = ref('');
const amount = ref('');

const coinList = computed(() => withdrawStore.list);
const selectedCoin = computed(() => withdrawStore.selectedCoin || {
  symbol: 'USDT',
  logo: '',
  balanceNormal: '0',
  chains: [
    {
      symbolType: 'BEP20',
      feeRate: '0.01',
      feeMin: '1'
    }
  ]
});
const chains = computed(() => selectedCoin.value.chains || []);
const selectedChain = computed(() => chains.value[selectedChainIndex.value] || {});
const minAmount = computed(() => Number(selectedChain.value.amountMin || selectedCoin.value.amountMin || 0));
const maxAmount = computed(() => Number(selectedChain.value.amountMax || selectedCoin.value.amountMax || 0));
const chainOptions = computed(() =>
  chains.value.map((item, index) => ({
    text: item.symbolType || item.chainName || `Network ${index + 1}`,
    value: index
  }))
);
const selectedChainLabel = computed(() => chainOptions.value[selectedChainIndex.value]?.text || '--');
const withdrawMinText = computed(() => `${formatTokenAmount(minAmount.value || 0)} ${selectedCoin.value.symbol || ''}`.trim());

const feeValue = computed(() => {
  const value = Number(amount.value || 0);
  const rate = normalizeFeeRate(selectedChain.value.feeRate);
  if (!value) {
    return 0;
  }
  return Math.max(value * rate);
});
const receivedAmount = computed(() =>
  formatTokenAmount(Math.max(Number(amount.value || 0) - feeValue.value, 0))
);
const feeRateText = computed(() => `${formatTokenAmount(normalizeFeeRate(selectedChain.value.feeRate) * 100)}%`);

watch(
  () => withdrawStore.selectedCoinIndex,
  () => {
    selectedChainIndex.value = 0;
    amount.value = '';
    showChainPopup.value = false;
  }
);

watch(
  () => pageState.value.scanResult,
  (value) => {
    if (!value) {
      return;
    }
    addressTo.value = String(value).split('&')[0];
    navigateWithState(router, route.path, {
      namespace: 'wallet-withdraw',
      replace: true,
      payload: {
        ...pageState.value,
        scanResult: ''
      }
    });
  },
  { immediate: true }
);

function toggleChainPopup() {
  if (chainOptions.value.length <= 1) {
    return;
  }
  showChainPopup.value = !showChainPopup.value;
}

function selectChain(index) {
  selectedChainIndex.value = index;
  showChainPopup.value = false;
}

function goSelectCoin() {
  router.push('/wallet/withdraw/select-coin');
}

function openScanner() {
  openWalletScanner({
    back: '/wallet/withdraw',
    stateNamespace: 'wallet-withdraw',
    statePayload: {
      ...pageState.value
    }
  });
}

function fillAll() {
  amount.value = String(selectedCoin.value.balanceNormal || selectedCoin.value.balance || '0');
}

async function confirmWithdraw() {
  const amountValue = Number(amount.value || 0);

  if (!addressTo.value.trim()) {
    showFailToast(t('wallet.enterWithdrawAddress'));
    return;
  }

  if (!amountValue) {
    showFailToast(t('card.enterValidAmount'));
    return;
  }

  if (minAmount.value && amountValue < minAmount.value) {
    showFailToast(`${t('card.minAmount')} ${formatTokenAmount(minAmount.value)} ${selectedCoin.value.symbol}`);
    return;
  }

  if (maxAmount.value && amountValue > maxAmount.value) {
    showFailToast(`${t('wallet.maxAmountLabel')} ${formatTokenAmount(maxAmount.value)} ${selectedCoin.value.symbol}`);
    return;
  }

  try {
    await showConfirmDialog({
      title: t('home.withdraw'),
      message: t('wallet.withdrawConfirm')
    });
  } catch (error) {
    return;
  }

  showLoadingToast({
    message: t('common.submitting'),
    forbidClick: true,
    duration: 0
  });

  try {
    await pcCardAssetWithdraw({
      address: profileStore.platformAddress || null,
      amount: amount.value,
      symbol: selectedCoin.value.symbol,
      symbolType: selectedChain.value.symbolType,
      addressTo: addressTo.value.trim()
    });
    closeToast();
    addressTo.value = '';
    amount.value = '';
    showPassedToast(t('wallet.withdrawSuccess'));
  } catch (error) {
    closeToast();
    showFailToast(error?.msg || t('common.retryLater'));
  }
}

async function loadData() {
  showLoadingToast({
    message: t('common.loading'),
    forbidClick: true,
    duration: 0
  });

  try {
    await withdrawStore.fetchWithdrawInfo({
      address: profileStore.platformAddress || null,
      reset: !withdrawStore.list.length
    });
    selectedChainIndex.value = 0;
    showChainPopup.value = false;
  } catch (error) {
    showFailToast(error?.msg || t('common.retryLater'));
  } finally {
    closeToast();
  }
}

onMounted(loadData);
</script>

<style scoped lang="scss">
.wallet-withdraw-page {
  min-height: var(--app-viewport-height);
  padding: 15px 14px 34px;
  background: #f7f7f7;
}

.wallet-card {
  margin-bottom: 10px;
  padding: 13px 16px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}
.pt-20 {
  padding-bottom: 20px;
}

.network-card {
  position: relative;
  z-index: 3;
}

.selector-card,
.amount-title,
.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.amount-title {
  span {
    font-weight: bold;
  }
}

.selector-card{
  span {
    display: flex;
    align-items: center;
  }
  &__arrow {
    width: 29px;
    height: 29px;
    margin-left: 4px;
    flex: 0 0 29px;
    background: var(--wp-color-theme);
    -webkit-mask: url('@/assets/images/img_account_right.png') center / contain no-repeat;
    mask: url('@/assets/images/img_account_right.png') center / contain no-repeat;
  }
}

.selector-card > span,
.amount-title span,
.detail-row span {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}



.coin-title {
  display: flex;
  align-items: center;

  .app-image {
    width: 40px;
    height: 40px;
    margin-right: 8px;
    border-radius: 50%;
    object-fit: cover;
  }

  [data-strong] {
    color: #000000;
    font-size: 16px;
    font-weight: 700;
  }
}

h2 {
  margin: 0 0 14px;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
}

.amount-title h2 {
  margin-bottom: 0;
}

.network-select,
.input-shell,
.amount-field {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 0 12px 0 16px;
  border-radius: 8px;
  background: #f7f7f7;
}

.network-select {
  justify-content: space-between;
  padding: 0 16px;
  color: #1d2129;
  font-size: 14px;
  cursor: pointer;

  :deep(.van-icon) {
    color: #909399;
    font-size: 18px;
  }
}

.network-options {
  position: absolute;
  top: 94px;
  left: 16px;
  right: 16px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.14);
  z-index: 5;

  &__item {
    width: 100%;
    padding: 10px 18px;
    border: 0;
    background: #ffffff;
    color: #1d2129;
    font-size: 14px;
    text-align: left;
  }

  &__item + &__item {
    border-top: 1px solid #f5f5f5;
  }

  &__item--active {
    background: #d9d9d9;
  }
}

.network-mask {
  position: fixed;
  inset: 0;
  z-index: 2;
  background: transparent;
}

.input-shell input,
.amount-field input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #000000;
  font-size: 14px;
  line-height: 20px;
}

.input-shell input::placeholder,
.amount-field input::placeholder {
  color: #999999;
}

.scan-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 25px;
    height: 25px;
    object-fit: contain;
  }
}

.amount-field {
  margin-top: 10px;
}

.amount-side {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
}

.amount-symbol {
  color: #000000;
  font-size: 16px;
  font-weight: 700;
}

.all-btn {
  padding: 8px 12px;
  border: 0;
  border-radius: 4px;
  background: var(--van-primary-color);
  color: #ffffff;
  font-size: 13px;
  line-height: 1;
}

.dashed-divider {
  width: 100%;
  height: 1px;
  margin: 15px 0;
  background-image: linear-gradient(to right, #dcdfe6 60%, rgba(255, 255, 255, 0) 0%);
  background-size: 8px 1px;
  background-repeat: repeat-x;
}

.detail-row {
  [data-strong] {
    color: #000000;
    font-size: 14px;
    font-weight: 700;
  }
}

.submit-btn {
  margin: 20px 0 30px;
}

.notes-card p {
  margin: 6px 0 0;
  color: #333333;
  font-size: 12px;
  line-height: 18px;

  [data-strong] {
    color: #000000;
    font-weight: 500;
  }
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;

  .app-empty-state__image {
    width: 120px;
    height: 120px;
  }

  span {
    margin-top: 12px;
    color: #333333;
    font-size: 14px;
  }
}
</style>
